import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, deleteDoc, orderBy, limit, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { Edit, Trash2, Plus, AlertCircle, Search, RefreshCw, User } from 'lucide-react';

function MyBrandsPage() {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState(null);
  const [authors, setAuthors] = useState({});
  const [promotionCounts, setPromotionCounts] = useState({});
  const [imageErrors, setImageErrors] = useState({});
  const [authorImageErrors, setAuthorImageErrors] = useState({});
  
  // Function to handle brand logo image errors
  const handleBrandImageError = (brandId) => {
    setImageErrors(prev => ({
      ...prev,
      [brandId]: true
    }));
  };

  // Function to handle author image errors
  const handleAuthorImageError = (authorId) => {
    setAuthorImageErrors(prev => ({
      ...prev,
      [authorId]: true
    }));
  };

  // Function to get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };
  
  // Function to fetch author information for brands (optimized)
  const fetchAuthors = async (brandsList) => {
    const authorIds = [...new Set(brandsList.map(brand => brand.createdBy))].filter(Boolean);
    const authorData = {};
    
    try {
      // Batch fetch all authors in parallel instead of sequential
      const authorPromises = authorIds.map(async (authorId) => {
        try {
          const userDoc = doc(db, "users", authorId);
          const userSnapshot = await getDoc(userDoc);
          
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            return {
              id: authorId,
              data: {
                displayName: userData.displayName || userData.name || 'Anonymous User',
                photoURL: userData.photoURL || null,
                email: userData.email || null
              }
            };
          } else {
            return {
              id: authorId,
              data: {
                displayName: 'Unknown User',
                photoURL: null,
                email: null
              }
            };
          }
        } catch (error) {
          console.error(`Error fetching author ${authorId}:`, error);
          return {
            id: authorId,
            data: {
              displayName: authorId.substring(0, 8) + '...',
              photoURL: null,
              email: null
            }
          };
        }
      });
      
      // Wait for all author fetches to complete
      const authorResults = await Promise.all(authorPromises);
      
      // Convert to object format
      authorResults.forEach(result => {
        authorData[result.id] = result.data;
      });
      
      setAuthors(authorData);
    } catch (error) {
      console.error("Error fetching author data:", error);
      setAuthors({});
    }
  };
  
  // Function to fetch promotion counts (optimized)
  const fetchPromotionCounts = async (brandsList) => {
    const counts = {};
    
    try {
      // Batch fetch all promotion counts in parallel
      const promotionPromises = brandsList.map(async (brand) => {
        if (brand.id) {
          try {
            const promotionsRef = collection(doc(db, "brands", brand.id), "promotions");
            const promotionsSnapshot = await getDocs(promotionsRef);
            return {
              brandId: brand.id,
              count: promotionsSnapshot.size
            };
          } catch (error) {
            console.error(`Error fetching promotions for brand ${brand.id}:`, error);
            return {
              brandId: brand.id,
              count: 0
            };
          }
        }
        return {
          brandId: brand.id,
          count: 0
        };
      });
      
      // Wait for all promotion count fetches to complete
      const promotionResults = await Promise.all(promotionPromises);
      
      // Convert to object format
      promotionResults.forEach(result => {
        counts[result.brandId] = result.count;
      });
      
      setPromotionCounts(counts);
    } catch (error) {
      console.error("Error fetching promotion counts:", error);
      setPromotionCounts({});
    }
  };
  
  const fetchBrands = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      let brandsQuery;
      
      if (userRole === 'admin') {
        // Admin can see all brands
        brandsQuery = query(
          collection(db, "brands"),
          orderBy("createdAt", "desc")
        );
      } else {
        // Regular users see only their brands
        brandsQuery = query(
          collection(db, "brands"),
          where("createdBy", "==", user.uid),
          orderBy("createdAt", "desc")
        );
      }
      
      const brandsSnapshot = await getDocs(brandsQuery);
      const brandsList = [];
      
      if (brandsSnapshot.empty) {
        setBrands([]);
        setAuthors({});
        setPromotionCounts({});
      } else {
        brandsSnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Generate slug if it doesn't exist (for backward compatibility)
          let slug = data.slug;
          if (!slug && data.name) {
            slug = data.name
              .toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/--+/g, '-')
              .trim();
          }
          
          brandsList.push({ 
            id: doc.id,
            ...data,
            slug: slug || 'no-slug',
            // Ensure rating is a number
            rating: typeof data.rating === 'string' ? parseFloat(data.rating) : data.rating
          });
        });
        
        // Set the brands immediately to show the list faster
        setBrands(brandsList);
        
        // Reset image error states when fetching new brands
        setImageErrors({});
        setAuthorImageErrors({});
        
        // Fetch additional data in parallel (not blocking the UI)
        Promise.all([
          fetchAuthors(brandsList),
          fetchPromotionCounts(brandsList)
        ]).catch(error => {
          console.error("Error fetching additional data:", error);
        });
      }
    } catch (error) {
      console.error("Error fetching brands:", error);
      setError("Failed to load brands. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch brands when dependencies change
  useEffect(() => {
    fetchBrands();
  }, [user, userRole]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleDeleteBrand = async (brandId) => {
    if (!user) return;
    
    // Store original state for potential rollback
    const originalBrands = brands;
    const originalPromotionCounts = promotionCounts;
    
    try {
      // Optimistic update - remove from UI immediately
      setBrands(brands.filter(brand => brand.id !== brandId));
      setConfirmDelete(null);
      
      // Remove from promotion counts
      const newPromotionCounts = { ...promotionCounts };
      delete newPromotionCounts[brandId];
      setPromotionCounts(newPromotionCounts);
      
      // Then delete from database
      const brandDocRef = doc(db, "brands", brandId);
      await deleteDoc(brandDocRef);
      
    } catch (error) {
      console.error("Error deleting brand:", error);
      setError("Failed to delete brand. Please try again.");
      
      // Revert optimistic update on error
      setBrands(originalBrands);
      setPromotionCounts(originalPromotionCounts);
    }
  };
  
  // Get author display name for a brand
  const getAuthorName = (brand) => {
    if (!brand.createdBy) return 'Unknown';
    
    if (brand.createdBy === user.uid) {
      return 'You';
    }
    
    if (authors[brand.createdBy]) {
      return authors[brand.createdBy].displayName;
    }
    
    // Fallback if author data wasn't loaded yet
    return 'Loading...';
  };
  
  // Filtering brands based on search term
  const filteredBrands = brands.filter(brand => 
    brand.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brand.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    brand.slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getAuthorName(brand).toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // If not logged in, show sign-in message
  if (!user) {
    return (
      <div className="p-4 max-w-6xl mx-auto flex flex-col items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Please Sign In</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view your brands</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="pb-8 p-4 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">
          {userRole === 'admin' ? 'All Brands' : 'My Brands'}
        </h2>
        
        <div className="flex gap-3">
          {/* Refresh Button */}
          <button
            onClick={fetchBrands}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 flex items-center"
            title="Refresh brands"
          >
            <RefreshCw size={16} className="mr-1" />
            Refresh
          </button>
          
          <Link 
            to="/create-brand" 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus size={16} className="mr-1" /> Create Brand
          </Link>
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button 
                onClick={fetchBrands}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center"
              >
                <RefreshCw size={14} className="mr-1" /> Try Again
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Search Bar */}
      <div className="mb-6 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search size={20} className="text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Search your brands..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      {/* Admin Badge */}
      {userRole === 'admin' && (
        <div className="mb-4 bg-purple-50 border-l-4 border-purple-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-800">
                Admin Dashboard
              </p>
              <p className="text-sm text-purple-700 mt-1">
                You are currently viewing all brands in the system.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading brands...</p>
        </div>
      ) : (
        <>
          {/* Brands List */}
          {filteredBrands.length > 0 ? (
            <div className="">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Brand
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        URL Slug
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Promo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Author
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredBrands.map((brand) => (
                      <tr key={brand.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden bg-gray-100">
                              {brand.logoImage && !imageErrors[brand.id] ? (
                                <img 
                                  src={brand.logoImage} 
                                  alt={brand.name} 
                                  className="h-full w-full object-cover"
                                  onError={() => handleBrandImageError(brand.id)}
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold text-lg">
                                  {getInitials(brand.name)}
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {brand.name || 'Unnamed Brand'}
                              </div>
                              <div className="text-sm text-gray-500 truncate max-w-xs">
                                {brand.description || 'No description'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-600">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-800">
                              {brand.slug || 'no-slug'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-blue-600 font-medium">
                            <div className="flex items-center">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-blue-100 text-blue-800">
                                {promotionCounts[brand.id] !== undefined ? promotionCounts[brand.id] : '...'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                            {brand.rating || '?'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 rounded-full overflow-hidden">
                              {authors[brand.createdBy]?.photoURL && !authorImageErrors[brand.createdBy] ? (
                                <img 
                                  src={authors[brand.createdBy].photoURL}
                                  alt={getAuthorName(brand)}
                                  className="h-full w-full object-cover"
                                  onError={() => handleAuthorImageError(brand.createdBy)}
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <div className="h-full w-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-bold text-xs">
                                  {brand.createdBy === user.uid ? 
                                    getInitials(user.displayName || user.email) : 
                                    getInitials(authors[brand.createdBy]?.displayName || 'U')
                                  }
                                </div>
                              )}
                            </div>
                            <div className="ml-2">
                              <div className="text-sm font-medium text-gray-900">
                                {brand.createdBy === user.uid ? (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">You</span>
                                ) : (
                                  getAuthorName(brand)
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Link
                              to={`/brand/${brand.slug}`}
                              target="_blank"
                              className="text-gray-600 hover:text-gray-900 bg-gray-100 p-2 rounded-full"
                              title="View Brand"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </Link>
                            <Link
                              to={`/edit-brand/${brand.id}`}
                              className="text-blue-600 hover:text-blue-900 bg-blue-100 p-2 rounded-full"
                              title="Edit Brand"
                            >
                              <Edit size={20} />
                            </Link>
                            <button
                              onClick={() => setConfirmDelete(brand.id)}
                              className="text-red-600 hover:text-red-900 bg-red-100 p-2 rounded-full"
                              title="Delete Brand"
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                          
                          {/* Delete Confirmation */}
                          {confirmDelete === brand.id && (
                            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                              <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
                                <div className="flex items-center mb-4">
                                  <AlertCircle size={24} className="text-red-500 mr-2 flex-shrink-0" />
                                  <h3 className="text-lg font-bold text-gray-900">Confirm Deletion</h3>
                                </div>
                                <p className="mb-6 text-gray-700 break-words">
                                  Are you sure you want to delete <strong>{brand.name}</strong>? 
                                </p>
                                <div className="flex justify-end space-x-3">
                                  <button
                                    onClick={() => setConfirmDelete(null)}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleDeleteBrand(brand.id)}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              {searchTerm ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mt-4 text-gray-600">No results found for "{searchTerm}"</p>
                  <p className="mt-2 text-gray-500">Try different keywords or clear your search.</p>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p className="mt-4 text-gray-600">You haven't created any brands yet.</p>
                  <p className="mt-2 text-gray-500">Get started by clicking the "Create Brand" button above.</p>
                  <div className="mt-6">
                    <Link
                      to="/create-brand"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={16} className="mr-2" /> Create Your First Brand
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MyBrandsPage;